import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/fsm';

const ContractForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { lang, t } = useLanguage();
  const f = t('fsm');
  const isRTL = lang === 'ar';

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [clientSearch, setClientSearch] = useState(undefined);
  const [showClientDrop, setShowClientDrop] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientEquipment, setClientEquipment] = useState([]);
  const [priceBooks, setPriceBooks] = useState([]);
  const [businessHours, setBusinessHours] = useState([]);
  const [equipmentIds, setEquipmentIds] = useState([]);

  const [formData, setFormData] = useState({
    client_id: '',
    title: '',
    title_ar: '',
    contract_type: 'maintenance',
    start_date: '',
    end_date: '',
    auto_renew: 0,
    visit_frequency: 'monthly',
    total_visits_allowed: '',
    sla_response_hours: '24',
    sla_resolution_hours: '72',
    sla_uptime_percent: '',
    coverage_scope: 'parts_and_labor',
    business_hours_id: '',
    contract_value: '',
    currency: 'EGP',
    payment_terms: '30',
    price_book_id: '',
    terms_conditions: '',
    notes: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const inputSt = {
    width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box'
  };

  const selectSt = { ...inputSt };

  const sectionSt = {
    backgroundColor: 'rgba(26, 32, 56, 0.8)', padding: '25px', borderRadius: '12px',
    marginBottom: '20px', border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const labelSt = { display: 'block', marginBottom: '8px', color: '#d1d5db', fontSize: '13px', fontWeight: '500' };

  const gridSt = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' };

  // ── Fetch lookups ──
  useEffect(() => {
    const token = localStorage.getItem('token');
    const h = { 'Authorization': `Bearer ${token}` };
    const fetchLookups = async () => {
      try {
        const [cRes, pbRes, bhRes] = await Promise.all([
          fetch(`${API}/clients?limit=500`, { headers: h }),
          fetch(`${API}/price-books`, { headers: h }).catch(() => null),
          fetch(`${API}/business-hours`, { headers: h }).catch(() => null),
        ]);
        if (cRes.ok) { const d = await cRes.json(); if (d.success) setClients(d.data || []); }
        if (pbRes?.ok) { const d = await pbRes.json(); if (d.success) setPriceBooks(d.data || []); }
        if (bhRes?.ok) { const d = await bhRes.json(); if (d.success) setBusinessHours(d.data || []); }
      } catch (err) { console.error('Lookups error:', err); }
    };
    fetchLookups();
  }, []);

  // ── Fetch contract for edit ──
  useEffect(() => {
    if (!isEdit) return;
    const token = localStorage.getItem('token');
    const fetchContract = async () => {
      try {
        const res = await fetch(`${API}/contracts/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data) {
            const c = data.data;
            setFormData({
              client_id: c.client_id || '',
              title: c.title || '',
              title_ar: c.title_ar || '',
              contract_type: c.contract_type || 'maintenance',
              start_date: c.start_date ? c.start_date.split('T')[0] : '',
              end_date: c.end_date ? c.end_date.split('T')[0] : '',
              auto_renew: c.auto_renew || 0,
              visit_frequency: c.visit_frequency || 'monthly',
              total_visits_allowed: c.total_visits_allowed || '',
              sla_response_hours: c.sla_response_hours || '24',
              sla_resolution_hours: c.sla_resolution_hours || '72',
              sla_uptime_percent: c.sla_uptime_percent || '',
              coverage_scope: c.coverage_scope || 'parts_and_labor',
              business_hours_id: c.business_hours_id || '',
              contract_value: c.contract_value || '',
              currency: c.currency || 'EGP',
              payment_terms: c.payment_terms || '30',
              price_book_id: c.price_book_id || '',
              terms_conditions: c.terms_conditions || '',
              notes: c.notes || ''
            });
            if (c.equipment && Array.isArray(c.equipment)) {
              setEquipmentIds(c.equipment.map(e => e.equipment_id || e.id));
            }
          }
        }
      } catch (err) { console.error('Fetch contract error:', err); }
      finally { setLoading(false); }
    };
    fetchContract();
  }, [id]);

  // ── Fetch client equipment ──
  useEffect(() => {
    if (!formData.client_id) { setClientEquipment([]); return; }
    const token = localStorage.getItem('token');
    const fetchEquip = async () => {
      try {
        const res = await fetch(`${API}/equipment?client_id=${formData.client_id}&limit=500`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) { const d = await res.json(); if (d.success) setClientEquipment(d.data || []); }
      } catch (err) { console.error('Equipment fetch error:', err); }
    };
    fetchEquip();
  }, [formData.client_id]);

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.client_id) { setError(f.err_select_client || 'Please select a client'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    if (!formData.start_date || !formData.end_date) { setError(f.err_dates_required || 'Start and end dates are required'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const body = { ...formData, equipment_ids: equipmentIds };
      const url = isEdit ? `${API}/contracts/${id}` : `${API}/contracts`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        navigate(isEdit ? `/fsm/contracts/${id}` : '/fsm/contracts');
      } else {
        setError(data.message || (f.save_failed || 'Failed to save'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      setError(f.network_err || 'Network error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally { setSaving(false); }
  };

  const toggleEquipment = (eId) => {
    setEquipmentIds(prev => prev.includes(eId) ? prev.filter(x => x !== eId) : [...prev, eId]);
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
          {f.loading || 'Loading...'}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '30px', direction: isRTL ? 'rtl' : 'ltr' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#f9fafb' }}>
            {isEdit ? (f.edit_contract || 'Edit Contract') : (f.new_contract || 'New Contract')}
          </h1>
          <button type="button" onClick={() => navigate('/fsm/contracts')} style={{
            padding: '10px 20px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.2)',
            backgroundColor: 'transparent', color: '#d1d5db', cursor: 'pointer', fontSize: '14px'
          }}>{f.cancel || 'Cancel'}</button>
        </div>

        {error && (
          <div style={{ padding: '15px', borderRadius: '8px', backgroundColor: 'rgba(220,53,69,0.15)',
            border: '1px solid rgba(220,53,69,0.3)', color: '#ff6b6b', marginBottom: '20px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* Client & Basic */}
          <div style={sectionSt}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              👤 {f.ct_client_info || 'Client & Basic Info'}
            </h3>
            <div style={gridSt}>
              <div>
                <label style={labelSt}>{f.ct_client || 'Client'} *</label>
                <div style={{position:'relative'}}>
                  <input type="text" style={selectSt} autoComplete="off"
                    placeholder={f.select_client || 'Search client...'}
                    value={clientSearch !== undefined ? clientSearch : (formData.client_id ? (clients.find(c=>String(c.id)===String(formData.client_id))?.[lang==='ar'?'name_ar':'name']||clients.find(c=>String(c.id)===String(formData.client_id))?.name||'') : '')}
                    onChange={(e) => { setClientSearch(e.target.value); setShowClientDrop(true); if(!e.target.value) handleChange('client_id',''); }}
                    onFocus={() => setShowClientDrop(true)}
                    onBlur={() => setTimeout(() => setShowClientDrop(false), 200)}
                  />
                  {showClientDrop && (
                    <div style={{position:'absolute',top:'100%',left:0,right:0,maxHeight:'250px',overflowY:'auto',background:'#1e293b',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'0 0 8px 8px',zIndex:50}}>
                      {clients.filter(c=>{if(!clientSearch)return true;const s=clientSearch.toLowerCase();return(c.name||'').toLowerCase().includes(s)||(c.name_ar||'').toLowerCase().includes(s);}).map(c => (
                        <div key={c.id} onClick={()=>{handleChange('client_id',String(c.id));setClientSearch(lang==='ar'?(c.name_ar||c.name):c.name);setShowClientDrop(false);}}
                          style={{padding:'10px 14px',cursor:'pointer',borderBottom:'1px solid rgba(255,255,255,0.05)',color:'#e2e8f0',fontSize:'13px'}}
                          onMouseEnter={e=>e.target.style.background='rgba(59,130,246,0.15)'}
                          onMouseLeave={e=>e.target.style.background='transparent'}>
                          {lang === 'ar' ? (c.name_ar || c.name) : c.name}
                        </div>
                      ))}
                      {clients.filter(c=>{if(!clientSearch)return true;const s=clientSearch.toLowerCase();return(c.name||'').toLowerCase().includes(s)||(c.name_ar||'').toLowerCase().includes(s);}).length===0 && (
                        <div style={{padding:'12px',color:'#64748b',textAlign:'center',fontSize:'13px'}}>{f.no_results || 'No clients found'}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label style={labelSt}>{f.ct_type || 'Contract Type'} *</label>
                <select value={formData.contract_type} onChange={(e) => handleChange('contract_type', e.target.value)} style={selectSt}>
                  <option value="warranty">{f.ctp_warranty || 'Warranty'}</option>
                  <option value="maintenance">{f.ctp_maintenance || 'Maintenance'}</option>
                  <option value="comprehensive">{f.ctp_full_service || 'Comprehensive'}</option>
                  <option value="on_demand">{f.ctp_on_demand || 'On Demand'}</option>
                </select>
              </div>
              <div>
                <label style={labelSt}>{f.ct_title || 'Contract Title'}</label>
                <input type="text" value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder={f.ct_title_placeholder || 'e.g. Annual Maintenance Contract'}
                  style={inputSt} />
              </div>
              <div>
                <label style={labelSt}>{f.ct_title_ar || 'Title (Arabic)'}</label>
                <input type="text" value={formData.title_ar}
                  onChange={(e) => handleChange('title_ar', e.target.value)}
                  placeholder="عقد صيانة سنوي"
                  style={{ ...inputSt, direction: 'rtl' }} />
              </div>
            </div>
          </div>

          {/* Coverage Period */}
          <div style={sectionSt}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              📅 {f.ct_coverage_period || 'Coverage Period'}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '15px' }}>
              <div>
                <label style={labelSt}>{f.ct_start || 'Start Date'} *</label>
                <input type="date" value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                  style={inputSt} />
              </div>
              <div>
                <label style={labelSt}>{f.ct_end || 'End Date'} *</label>
                <input type="date" value={formData.end_date}
                  onChange={(e) => handleChange('end_date', e.target.value)}
                  style={inputSt} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#d1d5db', fontSize: '14px' }}>
                  <input type="checkbox" checked={!!formData.auto_renew}
                    onChange={(e) => handleChange('auto_renew', e.target.checked ? 1 : 0)}
                    style={{ width: 18, height: 18 }} />
                  {f.ct_auto_renew || 'Auto-Renew'}
                </label>
              </div>
            </div>
          </div>

          {/* Visit Schedule */}
          <div style={sectionSt}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              🔧 {f.ct_visit_schedule || 'Visit Schedule'}
            </h3>
            <div style={gridSt}>
              <div>
                <label style={labelSt}>{f.ct_included_visits || 'Included Visits'}</label>
                <input type="text" value={formData.total_visits_allowed}
                  onChange={(e) => handleChange('total_visits_allowed', e.target.value)}
                  placeholder={f.ct_unlimited_if_empty || 'Leave empty for unlimited'}
                  style={inputSt} />
              </div>
              <div>
                <label style={labelSt}>{f.ct_billing || 'Frequency'}</label>
                <select value={formData.visit_frequency} onChange={(e) => handleChange('visit_frequency', e.target.value)} style={selectSt}>
                  <option value="monthly">{f.freq_monthly || 'Monthly'}</option>
                  <option value="quarterly">{f.freq_quarterly || 'Quarterly'}</option>
                  <option value="semi_annual">{f.freq_semi_annual || 'Semi-Annual'}</option>
                  <option value="annual">{f.freq_annual || 'Annual'}</option>
                  <option value="one_time">{f.freq_one_time || 'One-Time'}</option>
                </select>
              </div>
            </div>
          </div>

          {/* SLA Terms */}
          <div style={sectionSt}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              ⏱️ {f.ct_sla_terms || 'SLA Terms'}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
              <div>
                <label style={labelSt}>{f.ct_response_time || 'Response Time (hours)'}</label>
                <input type="text" value={formData.sla_response_hours}
                  onChange={(e) => handleChange('sla_response_hours', e.target.value)}
                  style={inputSt} />
              </div>
              <div>
                <label style={labelSt}>{f.ct_resolution_time || 'Resolution Time (hours)'}</label>
                <input type="text" value={formData.sla_resolution_hours}
                  onChange={(e) => handleChange('sla_resolution_hours', e.target.value)}
                  style={inputSt} />
              </div>
              <div>
                <label style={labelSt}>{f.ct_uptime || 'SLA Uptime %'}</label>
                <input type="text" value={formData.sla_uptime_percent}
                  onChange={(e) => handleChange('sla_uptime_percent', e.target.value)}
                  placeholder="99.9"
                  style={inputSt} />
              </div>
            </div>
            <div style={{ ...gridSt, marginTop: '15px' }}>
              <div>
                <label style={labelSt}>{f.ct_coverage || 'Coverage Scope'}</label>
                <select value={formData.coverage_scope} onChange={(e) => handleChange('coverage_scope', e.target.value)} style={selectSt}>
                  <option value="parts_and_labor">{f.cov_parts_labor || 'Parts & Labor'}</option>
                  <option value="labor_only">{f.cov_labor || 'Labor Only'}</option>
                  <option value="parts_only">{f.cov_parts || 'Parts Only'}</option>
                </select>
              </div>
              <div>
                <label style={labelSt}>{f.ct_business_hours || 'Business Hours Schedule'}</label>
                <select value={formData.business_hours_id} onChange={(e) => handleChange('business_hours_id', e.target.value)} style={selectSt}>
                  <option value="">{f.ct_default_hours || 'Default'}</option>
                  {businessHours.map(bh => <option key={bh.id} value={bh.id}>{bh.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Financial */}
          <div style={sectionSt}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              💰 {f.ct_financial || 'Financial'}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
              <div>
                <label style={labelSt}>{f.ct_value || 'Contract Value'}</label>
                <input type="text" value={formData.contract_value}
                  onChange={(e) => handleChange('contract_value', e.target.value)}
                  style={inputSt} />
              </div>
              <div>
                <label style={labelSt}>{f.ct_payment_terms || 'Payment Terms (days)'}</label>
                <input type="text" value={formData.payment_terms}
                  onChange={(e) => handleChange('payment_terms', e.target.value)}
                  style={inputSt} />
              </div>
              <div>
                <label style={labelSt}>{f.ct_price_book || 'Price Book'}</label>
                <select value={formData.price_book_id} onChange={(e) => handleChange('price_book_id', e.target.value)} style={selectSt}>
                  <option value="">{f.ct_default_pricing || 'Default Pricing'}</option>
                  {priceBooks.map(pb => <option key={pb.id} value={pb.id}>{pb.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Equipment Selection */}
          <div style={sectionSt}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              ⚙️ {f.ct_equipment_selection || 'Equipment'}
            </h3>
            {!formData.client_id ? (
              <p style={{ color: '#9ca3af', fontSize: '14px' }}>{f.ct_select_client_first || 'Select a client first to see their equipment'}</p>
            ) : clientEquipment.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: '14px' }}>{f.ct_no_client_equipment || 'No equipment found for this client'}</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                {clientEquipment.map(eq => {
                  const selected = equipmentIds.includes(eq.id);
                  return (
                    <div key={eq.id} onClick={() => toggleEquipment(eq.id)} style={{
                      padding: '12px', borderRadius: '8px', cursor: 'pointer',
                      border: `2px solid ${selected ? '#4285F4' : 'rgba(255,255,255,0.15)'}`,
                      backgroundColor: selected ? 'rgba(66,133,244,0.1)' : 'transparent',
                      transition: 'all 0.15s'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#f9fafb' }}>
                          {lang === 'ar' ? (eq.name_ar || eq.name) : eq.name}
                        </span>
                        <span style={{
                          width: 22, height: 22, borderRadius: 6,
                          border: `2px solid ${selected ? '#4285F4' : 'rgba(255,255,255,0.3)'}`,
                          backgroundColor: selected ? '#4285F4' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: '14px', fontWeight: '700'
                        }}>{selected ? '✓' : ''}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        {eq.serial_number && <span>S/N: {eq.serial_number} · </span>}
                        {eq.brand && <span>{eq.brand} {eq.model || ''}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {equipmentIds.length > 0 && (
              <p style={{ marginTop: '12px', fontSize: '13px', color: '#4285F4', fontWeight: '500' }}>
                {lang === 'ar' ? `${equipmentIds.length} معدات محددة` : `${equipmentIds.length} equipment selected`}
              </p>
            )}
          </div>

          {/* Terms & Notes */}
          <div style={sectionSt}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              📝 {f.ct_terms_notes || 'Terms & Notes'}
            </h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={labelSt}>{f.ct_terms || 'Terms & Conditions'}</label>
              <textarea value={formData.terms_conditions}
                onChange={(e) => handleChange('terms_conditions', e.target.value)}
                style={{ ...inputSt, minHeight: '80px', resize: 'vertical' }} />
            </div>
            <div>
              <label style={labelSt}>{f.ct_notes || 'Internal Notes'}</label>
              <textarea value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                style={{ ...inputSt, minHeight: '60px', resize: 'vertical' }} />
            </div>
          </div>

          {/* Submit */}
          <button type="submit" disabled={saving}
            style={{
              width: '100%', padding: '16px',
              backgroundColor: saving ? '#6c757d' : '#007bff',
              color: 'white', border: 'none', borderRadius: '10px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '17px', fontWeight: '700',
              boxShadow: '0 4px 14px rgba(0, 123, 255, 0.4)'
            }}>
            {saving ? '⏳ Saving...' : isEdit ? ('💾 ' + (f.save_changes || 'Save Changes')) : ('📄 ' + (f.create_contract || 'Create Contract'))}
          </button>

        </form>
      </div>
    </Layout>
  );
};

export default ContractForm;